import { exportTable } from './excel';
import React, { useCallback, useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ReportGridDensity, ReportGridExpansionState, ReportGridLabels, ReportGridProps } from './types';
import { AxisNode, buildReportGridModel, pathId, visibleColumns, visibleRows } from './core/model';
import { buildPresentation, normalizeSearch } from './core/presentation';
import { TableTitle, defaults, Frame, Message, Scroll, Table, Toolbar } from './ReportGrid.styles';

const labelsDefault: ReportGridLabels = {
  balance: 'Saldo acumulado', initialBalance: 'Saldo inicial',
  controls: 'Configurações da tabela', enterFullscreen: 'Tela inteira', exitFullscreen: 'Sair da tela inteira', fullscreenError: 'Não foi possível abrir a tela inteira neste navegador.',
  loading: 'Carregando dados…', empty: 'Nenhum dado disponível.', invalid: 'Não foi possível exibir a tabela.',
  compactView: 'Visualização compacta', comfortableView: 'Visualização confortável',
  clearSearch: 'Limpar busca',
  highlightMatches: 'Mostrar todas as linhas e destacar correspondências',
  search: 'Buscar na tabela', noMatches: 'Nenhuma linha encontrada.', searchHint: 'Busca nos nomes e valores numéricos das linhas. Totais e detalhamento mantêm os valores completos da base.',
  grandTotal: 'Total geral', subtotal: 'Total', missing: '—', nullDimension: 'Sem informação',
  expand: 'Expandir', collapse: 'Recolher', expandAll: 'Expandir tudo', collapseAll: 'Recolher tudo', details: 'Ver detalhes',
};
interface HeaderCell { node: AxisNode; span: number; rowSpan: number; subtotal?: boolean }
function headers(root: AxisNode, expanded: Set<string>, levels: number): HeaderCell[][] {
  const result: HeaderCell[][] = Array.from({ length: levels }, () => []);
  const visit = (node: AxisNode): number => {
    if (node.children.length && expanded.has(node.id)) {
      const cell: HeaderCell = { node, span: 0, rowSpan: 1 };
      result[node.depth - 1].push(cell);
      cell.span = node.children.reduce((sum, child) => sum + visit(child), 0) + 1;
      result[node.depth].push({ node, span: 1, rowSpan: levels - node.depth, subtotal: true });
      return cell.span;
    }
    result[node.depth - 1].push({ node, span: 1, rowSpan: levels - node.depth + 1 });
    return 1;
  };
  root.children.forEach(visit);
  return result;
}
type ContentProps = ReportGridProps & { density: ReportGridDensity; toggleDensity: () => void; fullscreen: boolean; fullscreenError: string; toggleFullscreen: () => void };
function useReportGridController(props: ContentProps) {
  const {
    dataset, locale = 'pt-BR', caption, height = 560, showRowTotals = true, showColumnTotals = true,
    defaultExpandedDepth = { rows: 0, columns: 0 }, expansion, onExpansionChange, onCellDoubleClick,
    formatDimension, formatValue,
  } = props;
  const labels = useMemo(() => ({ ...labelsDefault, ...props.labels }), [props.labels]);
  const hintId = useId();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const mayEdit = props.mode === 'edit' && props.canEdit === true;
  useEffect(() => { if (!mayEdit) setSettingsOpen(false); }, [mayEdit]);
  const searchInput = useRef<HTMLInputElement>(null);
  const settingsButton = useRef<HTMLButtonElement>(null);
  const searchOptions = typeof props.search === 'object' ? props.search : {};
  const searchEnabled = props.search === true || (typeof props.search === 'object' && props.search.enabled !== false);
  const [localHighlight, setLocalHighlight] = useState(searchOptions.defaultHighlightMatches ?? false);
  const highlight = searchOptions.highlightMatches ?? localHighlight;
  const [localQuery, setLocalQuery] = useState(searchOptions.defaultValue ?? '');
  const query = searchEnabled ? searchOptions.value ?? localQuery : '';
  const deferredQuery = useDeferredValue(query);
  // Clearing is immediate; expensive search results may trail normal typing.
  const resultQuery = query ? deferredQuery : '';
  const term = normalizeSearch(resultQuery, locale);
  const title = props.title ?? {};
  const titleText = title.text ?? caption ?? dataset.measure.label;
  // Invalid datasets become a local error state instead of breaking the host application.
  const result = useMemo(() => {
    try { return { model: buildReportGridModel(dataset), error: null }; }
    catch (e) { return { model: null, error: (e as Error).message }; }
  }, [dataset]);
  const model = result.model;
  const presentation = useMemo(() => model ? buildPresentation(model, dataset, locale, formatValue, formatDimension, labels.missing, labels.nullDimension, labels.grandTotal) : null,
    [model, dataset, locale, formatValue, formatDimension, labels.missing, labels.nullDimension, labels.grandTotal]);
  const matches = useMemo(() => presentation?.search(resultQuery) ?? { rows: new Set<string>(), cells: new Set() }, [presentation, resultQuery]);
  const signature = JSON.stringify([dataset.rowDimensions, dataset.columnDimensions]);
  const initialMode = props.initialExpansion;
  const initialDepth = initialMode === 'expanded' ? { rows: Infinity, columns: Infinity }
    : initialMode === 'collapsed' ? { rows: 0, columns: 0 }
    : initialMode ? { rows: initialMode.rows === 'all' ? Infinity : initialMode.rows, columns: initialMode.columns === 'all' ? Infinity : initialMode.columns }
    : defaultExpandedDepth;
  const initial = (): ReportGridExpansionState => ({
    rows: model?.rowNodes.filter(n => n.children.length && n.depth <= initialDepth.rows).map(n => n.id) ?? [],
    columns: model?.columnNodes.filter(n => n.children.length && n.depth <= initialDepth.columns).map(n => n.id) ?? [],
  });
  const [local, setLocal] = useState(() => ({ signature, model, value: initial() }));
  // React permits this guarded same-component adjustment; dimensions reset the default state.
  if (local.signature !== signature) setLocal({ signature, model, value: initial() });
  else if (local.model !== model) setLocal({ signature, model, value: {
    rows: local.value.rows.filter(id => model?.rowNodes.some(n => n.id === id)),
    columns: local.value.columns.filter(id => model?.columnNodes.some(n => n.id === id)),
  } });
  const state = expansion ?? (local.signature === signature ? local.value : initial());
  const current = useMemo<ReportGridExpansionState>(() => {
    const rowIds = new Set(model?.rowNodes.map(n => n.id));
    const columnIds = new Set(model?.columnNodes.map(n => n.id));
    return { rows: state.rows.filter(id => rowIds.has(id)), columns: state.columns.filter(id => columnIds.has(id)) };
  }, [model, state.rows, state.columns]);
  const update = useCallback((next: ReportGridExpansionState) => {
    if (!expansion) setLocal({ signature, model, value: next });
    onExpansionChange?.(next);
  }, [expansion, signature, model, onExpansionChange]);
  const toggle = useCallback((axis: 'rows' | 'columns', id: string) => update({ ...current,
    [axis]: current[axis].includes(id) ? current[axis].filter(value => value !== id) : [...current[axis], id],
  }), [current, update]);
  const stateMessage = props.loading ? <Message role="status">{labels.loading}</Message>
    : props.error || result.error ? <>
      {props.fullscreen && result.error && !props.error && <div className="pivot-fullscreen-bar"><button type="button" onClick={props.toggleFullscreen}>{labels.exitFullscreen}</button></div>}
      <Message role="alert">{labels.invalid} {props.error || result.error}</Message>
    </> : !model || !dataset.index.length || !dataset.columns.length ? <Message role="status">{labels.empty}</Message> : null;
  // The view below is isolated so hooks remain unconditional even for loading/invalid data.
  return { props, dataset, locale, caption, height, showRowTotals: showRowTotals && !term, showColumnTotals: showColumnTotals && !term,
    labels, hintId, settingsOpen, setSettingsOpen, mayEdit, settingsButton, searchInput, searchOptions,
    searchEnabled, highlight, setLocalHighlight, query, setLocalQuery, title, titleText, model, presentation,
    matches, term, current, update, toggle, stateMessage, onCellDoubleClick };
}

function ReportGridContent(props: ContentProps) {
  const view = useReportGridController(props);
  return <ReportGridResolvedView {...view} />;
}
function ReportGridResolvedView(view: ReturnType<typeof useReportGridController>): React.ReactElement {
  const { props, dataset, height, showRowTotals, showColumnTotals, labels, hintId,
    settingsOpen, setSettingsOpen, mayEdit, settingsButton, searchInput, searchOptions,
    searchEnabled, highlight, setLocalHighlight, query, setLocalQuery, title, titleText, matches,
    term, current, update, toggle, stateMessage, onCellDoubleClick } = view;
  const tableRef = useRef<HTMLTableElement>(null);
  const model = view.model;
  const presentation = view.presentation;
  const showBalance = props.showBalance ?? true;
  const striped = props.appearance?.striped;
  const maxCellWidth = props.appearance?.cellMaxWidth?.trim();
  const maxCellHeight = props.appearance?.cellMaxHeight?.trim();
  const clipped = !!(maxCellWidth || maxCellHeight);
  // Build the expensive grid only when result inputs change, never on urgent keystrokes.
  const grid = useMemo(() => {
    if (!model || !presentation) return null;
  const rowExpanded = new Set(current.rows), columnExpanded = new Set(current.columns);
  const label = presentation.label;
  const tooltip = (node: AxisNode, axis: 'rows' | 'columns') => {
    const dimensions = axis === 'rows' ? dataset.rowDimensions : dataset.columnDimensions;
    return dimensions[node.depth - 1]?.tooltipByPath?.[JSON.stringify(node.path)];
  };
  const matchingIds = matches.rows;
  const filterTree = (node: AxisNode): AxisNode | null => {
    if (matchingIds.has(node.id)) return node;
    const children = node.children.map(filterTree).filter((child): child is AxisNode => child !== null);
    return children.length ? { ...node, children } : null;
  };
  const searchedRoot = term && !highlight ? filterTree(model.rows) : model.rows;
  const effectiveRowExpanded = term ? new Set(model.rowNodes.map(n => n.id)) : rowExpanded;
  const rows = searchedRoot ? visibleRows(searchedRoot, effectiveRowExpanded) : [];
  const columns = visibleColumns(model.columns, columnExpanded);
  const hasMatches = rows.length > 0;
  if (showColumnTotals && hasMatches) rows.push(model.rows);
  if (showRowTotals) columns.push(model.columns);
  const expandableRows = model.rowNodes.filter(node => node.children.length);
  const expandableColumns = model.columnNodes.filter(node => node.children.length);
  const hasExpandableGroups = expandableRows.length + expandableColumns.length > 0;
  // Derive the global state from both axes, including externally controlled expansion.
  const allExpanded = hasExpandableGroups && expandableRows.every(node => rowExpanded.has(node.id))
    && expandableColumns.every(node => columnExpanded.has(node.id));
  const toggleAll = () => update(allExpanded ? { rows: [], columns: [] } : {
    rows: expandableRows.map(node => node.id), columns: expandableColumns.map(node => node.id),
  });
  const headerRows = headers(model.columns, columnExpanded, dataset.columnDimensions.length);
  const expandButton = (node: AxisNode, axis: 'rows' | 'columns') => {
    const expanded = (axis === 'rows' ? effectiveRowExpanded : columnExpanded).has(node.id);
    return <button type="button" className="pivot-toggle" aria-expanded={expanded}
      aria-label={`${expanded ? labels.collapse : labels.expand} ${label(node, axis)}`}
      disabled={axis === 'rows' && !!term} onClick={() => toggle(axis, node.id)}>{expanded ? '▾' : '▸'}</button>;
  };
  const headerTitle = (node: AxisNode, axis: 'rows' | 'columns') => clipped ? [label(node, axis), tooltip(node, axis)].filter(Boolean).join('\n') : tooltip(node, axis);
  return { allExpanded, hasExpandableGroups, toggleAll, table: (
    <Scroll $height={height} tabIndex={0} role="region" aria-label={titleText}>
      <Table ref={tableRef} $striped={striped} $maxCellWidth={maxCellWidth} $maxCellHeight={maxCellHeight} $columnCount={columns.length + 1} aria-label={titleText}>
        {maxCellWidth && <colgroup>
          <col className="pivot-row-column" />
          <col className="pivot-value-column" span={columns.length} />
        </colgroup>}
        <thead>{headerRows.map((cells, depth) => <tr key={depth}>
          {depth === 0 && <th className="pivot-corner" title={clipped ? dataset.rowDimensions.map(d => d.label).join(' / ') : undefined} rowSpan={headerRows.length} scope="col"><span className="pivot-header-content">{dataset.rowDimensions.map(d => d.label).join(' / ')}</span></th>}
          {cells.map(cell => <th key={`${cell.node.id}-${cell.subtotal ? 'subtotal' : 'group'}`}
            data-excel-label={cell.subtotal ? `${label(cell.node, 'columns')} (${labels.subtotal})` : undefined}
            title={headerTitle(cell.node, 'columns')} id={`${hintId}-col-${encodeURIComponent(cell.node.id)}-${cell.subtotal ? 'total' : 'head'}`}
            className={cell.subtotal || (cell.node.children.length && !columnExpanded.has(cell.node.id)) ? 'pivot-subtotal-column' : undefined}
            colSpan={cell.span} rowSpan={cell.rowSpan} scope={cell.span > 1 ? 'colgroup' : 'col'}>
            <span className="pivot-header-content">{!cell.subtotal && cell.node.children.length > 0 && expandButton(cell.node, 'columns')}
            {cell.subtotal ? `${labels.subtotal} ${label(cell.node, 'columns')}` : label(cell.node, 'columns')}</span>
          </th>)}
          {depth === 0 && showRowTotals && <th id={`${hintId}-grand-column`} rowSpan={headerRows.length} scope="col" className="pivot-total-column" title={clipped ? labels.grandTotal : undefined}><span className="pivot-header-content">{labels.grandTotal}</span></th>}
        </tr>)}</thead>
        <tbody>{!hasMatches && <tr><td colSpan={columns.length + 1}><Message role="status">{labels.noMatches}</Message></td></tr>}{rows.map(row => <tr key={row.id} className={[!row.depth ? 'pivot-grand' : row.children.length ? 'pivot-group' : '', term && highlight && matchingIds.has(row.id) ? 'pivot-match' : ''].join(' ')}>
          <th scope="row" title={headerTitle(row, 'rows')} id={`${hintId}-row-${encodeURIComponent(row.id)}`}><div style={{ paddingLeft: 12 + Math.max(0, row.depth - 1) * 20 }}>
            {row.children.length && row.depth ? expandButton(row, 'rows') : <span className="pivot-leaf" />}
            <span className="pivot-label">{label(row, 'rows')}</span>
            {!!row.depth && !!row.children.length && <span className="pivot-subtotal-label">{labels.subtotal}</span>}
          </div></th>
          {columns.map(column => {
            const cell = presentation.cell(row, column);
            const { value, text } = cell;
            const cellMatches = matches.cells.has(cell);
            const detail = () => { if (value !== null) onCellDoubleClick?.(model.context(row, column)); };
            const columnHeaderIds = column.depth ? column.path.map((_, i) => {
              const ancestorId = pathId(column.path.slice(0, i + 1));
              return `${hintId}-col-${encodeURIComponent(ancestorId)}-head`;
            }) : [`${hintId}-grand-column`];
            if (column.children.length && columnExpanded.has(column.id)) columnHeaderIds.push(`${hintId}-col-${encodeURIComponent(column.id)}-total`);
            return <td title={clipped ? text : undefined} data-excel-value={value ?? ''} key={column.id} headers={[`${hintId}-row-${encodeURIComponent(row.id)}`, ...columnHeaderIds].join(' ')}
              className={[!column.depth ? 'pivot-total-column' : column.children.length ? 'pivot-subtotal-column' : '', value?.startsWith('-') ? 'pivot-negative' : '', value === null ? 'pivot-missing' : '', cellMatches ? 'pivot-cell-match' : ''].join(' ')}>
              {onCellDoubleClick && value !== null ? <button type="button" aria-label={`${labels.details}: ${row.path.map(String).join(' / ') || labels.grandTotal}; ${column.path.map(String).join(' / ') || labels.grandTotal}; ${text}`}
                title={clipped ? `${text} — ${labels.details}` : labels.details} onDoubleClick={detail} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); detail(); } }}
                onClick={e => { if (e.detail === 0 || (e.nativeEvent as PointerEvent).pointerType === 'touch') detail(); }}>{text}</button> : <span>{text}</span>}
            </td>;
          })}
        </tr>)}</tbody>
        {showBalance && model.balance && presentation.balance && <tfoot>
          <tr className="pivot-balance" data-row-type="balance">
            <th scope="row" id={`${hintId}-balance`} title={`${clipped ? `${model.balance.label ?? labels.balance}\n` : ''}${labels.initialBalance}: ${presentation.balance.initial.text}`}>
              <div>{model.balance.label ?? labels.balance}<small>{labels.initialBalance}: {presentation.balance.initial.text}</small></div>
            </th>
            {columns.map(column => {
              const cell = presentation.balance!.cell(column);
              return <td title={clipped ? cell.text : undefined} data-excel-value={cell.value ?? ''} key={column.id} headers={`${hintId}-balance`} className={[matches.cells.has(cell) ? 'pivot-cell-match' : '', cell.value?.startsWith('-') ? 'pivot-negative' : ''].join(' ')}><span>{cell.text}</span></td>;
            })}
          </tr>
        </tfoot>}
      </Table>
    </Scroll>) };
  }, [model, presentation, matches, term, highlight, current, height, showRowTotals,
    showColumnTotals, showBalance, onCellDoubleClick, striped, maxCellWidth, maxCellHeight, clipped, titleText, labels, hintId, dataset, update, toggle]);
  if (stateMessage) return <>
    {mayEdit && <Toolbar aria-label="Controles da tabela" onKeyDown={event => {
      if (event.key === 'Escape' && settingsOpen) { setSettingsOpen(false); settingsButton.current?.focus(); }
    }}>
      <div className="pivot-toolbar-row">
        <TableTitle $options={title} title={titleText}>{titleText}</TableTitle>
        <button ref={settingsButton} type="button" className="pivot-icon-button"
          aria-label={labels.controls} title={labels.controls}
          aria-haspopup={props.onRequestSettings ? 'dialog' : undefined}
          aria-expanded={props.onRequestSettings ? undefined : settingsOpen}
          aria-controls={props.onRequestSettings ? undefined : `${hintId}-settings`}
          onClick={() => {
            if (props.onRequestSettings) props.onRequestSettings();
            else setSettingsOpen(open => !open);
          }}>
          <span aria-hidden="true">⚙</span>
        </button>
      </div>
      {!props.onRequestSettings && settingsOpen && <div id={`${hintId}-settings`} className="pivot-settings-panel" role="region" aria-label={labels.controls}>
        {props.controls}
      </div>}
    </Toolbar>}
    {stateMessage}
  </>;
  if (!grid) return <></>;
  const { allExpanded, hasExpandableGroups, toggleAll } = grid;
  return <>
    <Toolbar aria-label="Controles da tabela" onKeyDown={event => {
      if (event.key === 'Escape' && settingsOpen) { setSettingsOpen(false); settingsButton.current?.focus(); }
    }}>
      <div className="pivot-toolbar-row">
        <TableTitle $options={title} title={titleText}>{titleText}{title.subtitle && <span>{title.subtitle}</span>}</TableTitle>
        {searchEnabled && <div className="pivot-search">
          <input ref={searchInput} id={`${hintId}-search`} aria-label={labels.search} type="search" value={query} placeholder={searchOptions.placeholder ?? labels.search}
            aria-describedby={term ? `${hintId}-search-hint` : undefined}
            onChange={event => { const value = event.target.value; if (searchOptions.value === undefined) setLocalQuery(value); searchOptions.onChange?.(value); }} />
          <button type="button" className="pivot-search-mode" aria-label={labels.highlightMatches} title={labels.highlightMatches}
            aria-pressed={highlight} onClick={() => {
              if (searchOptions.highlightMatches === undefined) setLocalHighlight(!highlight);
              searchOptions.onHighlightMatchesChange?.(!highlight);
            }}>
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" />
              {!highlight && <path d="m3 3 18 18" />}
            </svg>
          </button>
          {query.length > 0 && <button type="button" className="pivot-search-clear" aria-label={labels.clearSearch} title={labels.clearSearch}
            onClick={() => {
              if (searchOptions.value === undefined) setLocalQuery('');
              searchOptions.onChange?.('');
              searchInput.current?.focus();
            }}><span aria-hidden="true">×</span></button>}
        </div>}
        <button type="button" className="pivot-icon-button" aria-label={allExpanded ? labels.collapseAll : labels.expandAll}
          title={allExpanded ? labels.collapseAll : labels.expandAll} aria-pressed={allExpanded}
          disabled={!hasExpandableGroups} onClick={toggleAll}>
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            {allExpanded
              ? <path d="M12 3v7m-4-4 4 4 4-4M12 21v-7m-4 4 4-4 4 4" />
              : <path d="M12 10V3m-4 4 4-4 4 4M12 14v7m-4-4 4 4 4-4" />}
          </svg>
        </button>
        <button type="button" className="pivot-icon-button" aria-label={props.density === 'compact' ? labels.comfortableView : labels.compactView}
          title={props.density === 'compact' ? labels.comfortableView : labels.compactView} aria-pressed={props.density === 'compact'} onClick={props.toggleDensity}>
          <svg aria-hidden="true" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
            <rect x="3" y="3" width="18" height="18" rx="1" /><path d={props.density === 'compact' ? 'M3 7h18M3 11h18M3 15h18M3 19h18M9 3v18M15 3v18' : 'M3 9h18M3 15h18M11 3v18'} />
          </svg>
        </button>
        {props.enableExport !== false && <button type="button" className="pivot-icon-button" aria-label="Exportar Excel" title="Exportar Excel"
          onClick={() => { if (tableRef.current) exportTable(tableRef.current, titleText); }}>
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3v12m-4-4 4 4 4-4M4 16v5h16v-5" /></svg>
        </button>}
        {mayEdit && <button ref={settingsButton} type="button" className="pivot-icon-button" aria-label={labels.controls} title={labels.controls}
          aria-expanded={props.onRequestSettings ? undefined : settingsOpen} aria-haspopup={props.onRequestSettings ? 'dialog' : undefined}
          aria-controls={props.onRequestSettings ? undefined : `${hintId}-settings`} onClick={() => { if (mayEdit) { if (props.onRequestSettings) props.onRequestSettings(); else setSettingsOpen(open => !open); } }}>
          <span aria-hidden="true">⚙</span>
        </button>}
        {(props.enableFullscreen !== false || props.fullscreen) && <button type="button" className="pivot-icon-button"
          aria-label={props.fullscreen ? labels.exitFullscreen : labels.enterFullscreen}
          title={props.fullscreen ? labels.exitFullscreen : labels.enterFullscreen} onClick={props.toggleFullscreen}>
          <span aria-hidden="true">{props.fullscreen ? '⊡' : '⛶'}</span>
        </button>}
      </div>
      {mayEdit && !props.onRequestSettings && settingsOpen && <div id={`${hintId}-settings`} className="pivot-settings-panel" role="region" aria-label={labels.controls}>
        {props.controls}
      </div>}
      {props.fullscreenError && <p role="alert">{labels.fullscreenError}</p>}
      {term && <p id={`${hintId}-search-hint`} role="status">{highlight && !matches.rows.size ? `${labels.noMatches} ` : ''}{labels.searchHint}</p>}
    </Toolbar>
    {grid.table}
  </>;
}
/** Portable view: no fetching, routing, globals, or dependency on the demo page. */
export function ReportGrid(props: ReportGridProps) {
  const [localDensity, setLocalDensity] = useState<ReportGridDensity>(props.defaultDensity ?? 'comfortable');
  const density = props.density ?? localDensity;
  const toggleDensity = () => {
    const next = density === 'compact' ? 'comfortable' : 'compact';
    if (props.density === undefined) setLocalDensity(next);
    props.onDensityChange?.(next);
  };
  const frame = useRef<HTMLDivElement>(null);
  const fullscreenTrigger = useRef<HTMLElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState('');
  const activeFullscreen = useRef(false);
  const onFullscreenChange = useRef(props.onFullscreenChange);
  onFullscreenChange.current = props.onFullscreenChange;
  useEffect(() => {
    const change = () => {
      const active = document.fullscreenElement === frame.current;
      if (active === activeFullscreen.current) return;
      activeFullscreen.current = active;
      setFullscreen(active);
      onFullscreenChange.current?.(active);
      if (!active && fullscreenTrigger.current?.isConnected) fullscreenTrigger.current.focus();
    };
    document.addEventListener('fullscreenchange', change);
    return () => document.removeEventListener('fullscreenchange', change);
  }, []);
  const toggleFullscreen = async () => {
    setFullscreenError('');
    try {
      if (document.fullscreenElement === frame.current) await document.exitFullscreen();
      else {
        fullscreenTrigger.current = document.activeElement as HTMLElement;
        if (!frame.current?.requestFullscreen) throw new Error('Fullscreen API unavailable');
        await frame.current.requestFullscreen();
      }
    } catch { setFullscreenError(labelsDefault.fullscreenError); }
  };
  const appearance = { ...defaults, ...props.appearance,
    // Preserve the original shared subtotal token for existing consumers.
    rowSubtotalBackground: props.appearance?.rowSubtotalBackground ?? props.appearance?.subtotal ?? defaults.rowSubtotalBackground,
    columnSubtotalBackground: props.appearance?.columnSubtotalBackground ?? props.appearance?.subtotal ?? defaults.columnSubtotalBackground,
  };
  return <Frame ref={frame} data-density={density} className={props.className} style={{ width: props.width }} $appearance={appearance}>
    {fullscreen && (props.loading || props.error || !props.dataset.index.length || !props.dataset.columns.length) && <div className="pivot-fullscreen-bar"><button type="button" onClick={toggleFullscreen}>{props.labels?.exitFullscreen ?? labelsDefault.exitFullscreen}</button></div>}
    <ReportGridContent {...props} density={density} toggleDensity={toggleDensity} fullscreen={fullscreen} fullscreenError={fullscreenError} toggleFullscreen={toggleFullscreen} />
  </Frame>;
}
